import { useState } from "react";
import { Smartphone, Bell, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { joinWaitlist } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

// Accurate, professional store badges (black pill, real Apple logo + Google Play
// triangle). Non-linking "coming soon" variants until the apps are published.
const AppStoreBadge = () => (
  <div data-testid="badge-appstore" className="inline-flex items-center gap-2.5 rounded-xl border border-white/25 bg-black px-4 py-2.5 transition-transform hover:scale-[1.03]">
    <svg viewBox="0 0 384 512" className="h-7 w-7 text-white" fill="currentColor" aria-hidden="true">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zM262.1 104.5c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
    <span className="text-left leading-tight">
      <span className="block text-[10px] text-white/70">Download on the</span>
      <span className="block text-base font-semibold text-white">App Store</span>
    </span>
  </div>
);

const GooglePlayBadge = () => (
  <div data-testid="badge-playstore" className="inline-flex items-center gap-2.5 rounded-xl border border-white/25 bg-black px-4 py-2.5 transition-transform hover:scale-[1.03]">
    <svg viewBox="0 0 512 512" className="h-6 w-6" aria-hidden="true">
      <path fill="#00D2FF" d="M47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0z" />
      <path fill="#00E676" d="M325.3 234.3 104.6 13l280.8 161.2-60.1 60.1z" />
      <path fill="#FFCE00" d="M472.2 225.6c29.2 15.2 29.2 41.3 2 62.7l-90.9 52.8-65.7-64.5 65.7-64.5 88.9 13.5z" />
      <path fill="#FF3D47" d="M104.6 499l280.8-161.2-60.1-60.1L104.6 499z" />
    </svg>
    <span className="text-left leading-tight">
      <span className="block text-[10px] text-white/70">Get it on</span>
      <span className="block text-base font-semibold text-white">Google Play</span>
    </span>
  </div>
);

export default function AppLaunchBanner() {
  const { pick } = useLang();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const notify = async (e) => {
    e?.preventDefault();
    const em = email.trim();
    if (!em || !em.includes("@")) { toast.error(pick("कृपया सही ईमेल दर्ज करें", "Please enter a valid email")); return; }
    setBusy(true);
    try {
      await joinWaitlist({ email: em, source: "app_launch" });
      setDone(true);
      toast.success(pick("धन्यवाद! हम आपको सूचित करेंगे।", "You're on the list — we'll notify you!"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || pick("कुछ गलत हो गया", "Something went wrong"));
    } finally { setBusy(false); }
  };

  return (
    <section className="mx-auto max-w-7xl px-5 py-10 md:px-8" data-testid="app-launch-banner">
      <div className="relative overflow-hidden rounded-3xl border border-[#4a2f14] bg-gradient-to-br from-[#241405] via-[#3a2109] to-[#140b04] p-7 md:p-14">
        {/* decorative */}
        <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-[var(--saffron)] opacity-[0.18] blur-3xl" />
        <div className="pointer-events-none absolute -bottom-28 left-10 h-64 w-64 rounded-full bg-[var(--gold)] opacity-10 blur-3xl" />
        <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "22px 22px" }} />

        <div className="relative grid items-center gap-10 md:grid-cols-[1.35fr_1fr]">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-[var(--gold)]/40 bg-[var(--gold)]/10 px-3.5 py-1 text-xs font-medium uppercase tracking-[0.15em] text-[var(--gold)]">
              <Smartphone className="h-3.5 w-3.5" /> {pick("जल्द आ रहा है", "Coming soon")}
            </span>
            <h2 className="mt-5 font-serif-latin text-3xl leading-[1.15] text-white sm:text-4xl lg:text-5xl">
              {pick("धर्मार्थ ऐप — जल्द ही आपके फ़ोन पर", "Dharmartha, now in your pocket")}
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/70 md:text-base">
              {pick(
                "कथाएँ, मंत्र, सटीक पंचांग, जप माला और बहुत कुछ — कभी भी, कहीं भी। लॉन्च होते ही सबसे पहले जानें।",
                "Kathas, mantras, your exact Panchangam, a japa mala and more — anytime, anywhere. Be the first to know the moment it launches.")}
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <AppStoreBadge />
              <GooglePlayBadge />
            </div>

            {done ? (
              <div className="mt-7 inline-flex items-center gap-2 rounded-xl border border-green-400/30 bg-green-400/10 px-4 py-3 text-sm text-green-300" data-testid="app-notify-done">
                <Check className="h-4 w-4" /> {pick("आप सूची में हैं! लॉन्च पर ईमेल मिलेगा।", "You're on the list! We'll email you at launch.")}
              </div>
            ) : (
              <form onSubmit={notify} className="mt-7 flex w-full max-w-md flex-col gap-2.5 sm:flex-row">
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="app-notify-email" placeholder={pick("आपका ईमेल", "Your email address")}
                  className="min-w-0 flex-1 rounded-xl border border-white/20 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-[var(--saffron)] focus:outline-none"
                />
                <button type="submit" disabled={busy} data-testid="app-notify-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--saffron)] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />} {pick("सूचित करें", "Notify me")}
                </button>
              </form>
            )}
            <p className="mt-2.5 text-xs text-white/45">{pick("कोई स्पैम नहीं। केवल लॉन्च की सूचना।", "No spam — just a launch heads-up.")}</p>
          </div>

          {/* phone mockup */}
          <div className="hidden justify-center md:flex">
            <div className="relative h-[20rem] w-[10rem] rounded-[2.2rem] border-[5px] border-white/15 bg-black p-2 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.7)]">
              <div className="absolute left-1/2 top-3 z-10 h-1.5 w-14 -translate-x-1/2 rounded-full bg-white/20" />
              <div className="flex h-full w-full flex-col items-center justify-center rounded-[1.7rem] bg-gradient-to-b from-[#3a2109] via-[#2a1708] to-[#140b04]">
                <span className="font-deva text-5xl text-[var(--gold)]">ॐ</span>
                <span className="mt-3 font-serif-latin text-xl text-white">धर्मार्थ</span>
                <span className="mt-1 text-[10px] uppercase tracking-[0.25em] text-white/50">Dharmartha</span>
                <span className="mt-5 h-px w-12 bg-white/15" />
                <span className="mt-4 text-[10px] text-white/40">{pick("पंचांग · जप · कथा", "Panchangam · Japa · Katha")}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

